import type {
  Client,
  HumanTaskSearch,
  HumanTaskEntry,
  HumanTaskTemplate,
} from "../../../open-api";
import { HumanTask } from "../../../open-api/generated";
import { handleSdkError } from "../../helpers/errors";
import { DEFAULT_POLL_INTERVAL, EMPTY_SEARCH } from "./constants";
import { ClaimTaskOptions, PollIntervalOptions, UserType } from "./types";

export class HumanExecutor {
  public readonly _client: Client;

  constructor(client: Client) {
    this._client = client;
  }

  /**
   * @deprecated use search instead
   * Takes a set of filter parameters. return matches of human tasks for that set of parameters
   * @param state
   * @param assignee
   * @param assigneeType
   * @param claimedBy
   * @param taskName
   * @param freeText
   * @param includeInputOutput
   * @returns
   */
  public async getTasksByFilter(
    state: "PENDING" | "ASSIGNED" | "IN_PROGRESS" | "COMPLETED" | "TIMED_OUT",
    assignee?: string,
    assigneeType?:
      | "EXTERNAL_USER"
      | "EXTERNAL_GROUP"
      | "CONDUCTOR_USER"
      | "CONDUCTOR_GROUP",
    claimedBy?: string,
    taskName?: string,
    taskInputQuery?: string,
    taskOutputQuery?: string
  ): Promise<HumanTaskEntry[]> {
    const [claimedUserType, claimedUser] = claimedBy?.split(":") ?? [];

    if (claimedUserType && !claimedUser) {
      throw new Error("claimedBy should be in the format of <userType>:<user>");
    }

    const response = await this.search({
      states: [state],
      assignees: assignee ? [{ userType: assigneeType, user: assignee }] : [],
      claimants: claimedBy
        ? [{ userType: claimedUserType as UserType, user: claimedUser }]
        : [],
      taskRefNames: taskName ? [taskName] : [],
      taskInputQuery,
      taskOutputQuery,
    });

    return response;
  }

  /**
   * Takes a set of filter parameters. return matches of human tasks for that set of parameters
   * @param state
   * @param assignee
   * @param assigneeType
   * @param claimedBy
   * @param taskName
   * @param freeText
   * @param includeInputOutput
   * @returns Promise<HumanTaskEntry[]>
   */
  public async search(
    searchParams: Partial<HumanTaskSearch>
  ): Promise<HumanTaskEntry[]> {
    const search = { ...EMPTY_SEARCH, ...searchParams };
    try {
      const { data } = await HumanTask.search({
        client: this._client,
        body: search,
        throwOnError: true,
      });

      if (data?.results != undefined) {
        return data.results;
      }
      return [];
    } catch (error: unknown) {
      handleSdkError(error, "Failed to search human tasks");
    }
  }

  /**
   * Takes a set of filter parameters. An polling interval options. will poll until the task returns a result
   * @param state
   * @param assignee
   * @param assigneeType
   * @param claimedBy
   * @param taskName
   * @param freeText
   * @param includeInputOutput
   * @returns Promise<HumanTaskEntry[]>
   */
  public async pollSearch(
    searchParams: Partial<HumanTaskSearch>,
    {
      pollInterval = 100,
      maxPollTimes = 20,
    }: PollIntervalOptions = DEFAULT_POLL_INTERVAL
  ): Promise<HumanTaskEntry[]> {
    try {
      let pollCount = 0;
      while (pollCount < maxPollTimes) {
        const response = await this.search(searchParams);
        if (response.length > 0) {
          return response;
        }
        await new Promise((resolve) => setTimeout(resolve, pollInterval));
        pollCount++;
      }
      return [];
    } catch (error: unknown) {
      handleSdkError(error, "Failed to poll search human tasks");
    }
  }

  /**
   * Returns task for a given task id
   * @param taskId
   * @returns
   */
  public async getTaskById(taskId: string): Promise<HumanTaskEntry> {
    try {
      const { data } = await HumanTask.getTask1({
        client: this._client,
        path: { taskId },
        throwOnError: true,
      });

      return data;
    } catch (error: unknown) {
      handleSdkError(error, `Failed to get human task '${taskId}'`);
    }
  }

  /**
   * Assigns taskId to assignee. If the task is already assigned to another user, this will fail.
   * @param taskId
   * @param assignee
   * @returns
   */
  public async claimTaskAsExternalUser(
    taskId: string,
    assignee: string,
    options?: ClaimTaskOptions
  ): Promise<HumanTaskEntry> {
    try {
      const { data } = await HumanTask.assignAndClaim({
        client: this._client,
        path: { taskId, userId: assignee },
        query: {
          overrideAssignment: options?.overrideAssignment,
          withTemplate: options?.withTemplate,
        },
        throwOnError: true,
      });

      return data;
    } catch (error: unknown) {
      handleSdkError(
        error,
        `Failed to claim human task '${taskId}' as external user '${assignee}'`
      );
    }
  }

  /**
   * Claim task as conductor user
   * @param taskId
   * @returns
   */
  public async claimTaskAsConductorUser(
    taskId: string,
    options?: ClaimTaskOptions
  ): Promise<HumanTaskEntry> {
    try {
      const { data } = await HumanTask.claimTask({
        client: this._client,
        path: { taskId },
        query: {
          overrideAssignment: options?.overrideAssignment,
          withTemplate: options?.withTemplate,
        },
        throwOnError: true,
      });
      return data;
    } catch (error: unknown) {
      handleSdkError(
        error,
        `Failed to claim human task '${taskId}' as conductor user`
      );
    }
  }

  /**
   * Claim task as conductor user
   * @param taskId
   * @param assignee
   * @returns
   */
  public async releaseTask(taskId: string) {
    try {
      await HumanTask.releaseTask({
        client: this._client,
        path: { taskId },
        throwOnError: true,
      });
    } catch (error: unknown) {
      handleSdkError(error, `Failed to release human task '${taskId}'`);
    }
  }

  /**
   * Returns a HumanTaskTemplateEntry for a given name and version
   * @param templateId
   * @returns
   */
  public async getTemplateByNameVersion(
    name: string,
    version: number
  ): Promise<HumanTaskTemplate> {
    try {
      const { data } = await HumanTask.getTemplateByNameAndVersion({
        client: this._client,
        path: { name, version },
        throwOnError: true,
      });

      return data;
    } catch (error: unknown) {
      handleSdkError(
        error,
        `Failed to get template '${name}' version ${version}`
      );
    }
  }

  /**
   * @deprecated use getTemplate instead. name will be used as id here with version 1
   * Returns a HumanTaskTemplateEntry for a given templateId
   * @param templateId
   * @returns
   */
  public async getTemplateById(
    templateNameVersionOne: string
  ): Promise<HumanTaskTemplate> {
    return this.getTemplateByNameVersion(templateNameVersionOne, 1);
  }

  /**
   * Takes a taskId and a partial body. will update with given body
   * @param taskId
   * @param requestBody
   */
  public async updateTaskOutput(
    taskId: string,
    requestBody: Record<string, Record<string, unknown>>
  ): Promise<void> {
    try {
      await HumanTask.updateTaskOutput({
        client: this._client,
        path: { taskId },
        body: requestBody,
        query: { complete: false },
        throwOnError: true,
      });
    } catch (error: unknown) {
      handleSdkError(error, `Failed update human task '${taskId}' output`);
    }
  }

  /**
   * Takes a taskId and an optional partial body. will complete the task with the given body
   * @param taskId
   * @param requestBody
   */
  public async completeTask(
    taskId: string,
    requestBody: Record<string, Record<string, unknown>> = {}
  ) {
    try {
      await HumanTask.updateTaskOutput({
        client: this._client,
        path: { taskId },
        body: requestBody,
        query: { complete: true },
        throwOnError: true,
      });
    } catch (error: unknown) {
      handleSdkError(error, `Failed to complete human task '${taskId}'`);
    }
  }
}
